<script setup lang="ts">
import { computed } from 'vue'
import { useShipStore } from '@/stores/shipStore'
import { useGameStore } from '@/stores/gameStore'
import { useRouteStore } from '@/stores/routeStore'
import { CDN } from '@/models/cdn'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  shipId: {
    type: Number,
    required: true
  }
})

/**
 * Stores
 */
const shipStore = useShipStore()
const gameStore = useGameStore()
const routeStore = useRouteStore()

/**
 * Variables / Refs
 */
// Add your reactive variables here

/**
 * Computed
 */
const shipData = computed(() => shipStore.findShipById(props.shipId)!)

// Translate action name from backend
const translateActionName = (actionName: string) => {
  const key = `command.actionNames.${actionName}`
  const translated = t(key)
  // If translation key doesn't exist, return original
  return translated === key ? actionName : translated
}

// Enhanced action display logic
const actionDisplayText = computed(() => {
  if (shipData.value.currentActionName === 'Repairing' && shipData.value.actions.length > 0) {
    const repairAction = shipData.value.actions.find(action => action.action === 'repair')
    if (repairAction?.targetDay) {
      const daysRemaining = repairAction.targetDay - gameStore.turn
      if (daysRemaining <= 0) {
        return t('command.ships.current.repair.repairCompleted')
      }
      return t('command.ships.current.repair.repairingDaysLeft', { days: daysRemaining })
    }
  }
  return translateActionName(shipData.value.currentActionName)
})

// Check if ship is on a patrol route (but not if defend icon is already showing)
const isOnPatrolRoute = computed(() => {
  return shipData.value.routeId &&
    shipData.value.defendActionSet !== 1 &&
    shipData.value.currentActionName !== 'Patrol' &&
    shipData.value.currentActionName !== 'Patrol & Defend'
})

const patrolRouteTooltip = computed(() => {
  if (!shipData.value.routeId) return t('command.ships.current.patrol.tooltip')
  const route = routeStore.findRouteById(shipData.value.routeId)
  return route ? t('command.ships.current.patrol.patrolling', { name: route.name }) : t('command.ships.current.patrol.tooltip')
})

/**
 * Methods
 */
// Add your methods here

/**
 * Lifecycle hooks
 */
// Add lifecycle hooks if needed (onMounted, onUnmounted, etc.)
</script>

<template>
  <div>
    {{ actionDisplayText }}
    <span
      v-if="shipData.defendActionSet === 1 && shipData.currentActionName !== 'Defend'"
      class="pl-1"
    >
      <GenericIcon type="shield" :tooltip="t('command.ships.current.defend.tooltip')" />
    </span>
    <span
      v-if="isOnPatrolRoute"
      class="pl-1"
    >
      <img :src="CDN.icon32('patrol')" :alt="patrolRouteTooltip" :title="patrolRouteTooltip" class="inline w-5 h-5" />
    </span>
    <span v-if="shipData.actions.length > 1" class="pl-1">
      {{ shipData.actions.length > 1 ? '+' + (shipData.actions.length - 1) : '' }}
    </span>
  </div>
</template>

<style scoped lang="scss">
// Add your component-specific styles here
</style>
